import React from "react";

/**
 * Run-level `scope → dispatch → return` band.
 *
 * The one line that answers "is there anything to check here?" before any
 * drill-down. Deliberately at run level, not per fan-out: the launch scope is
 * requested once per run and then split across fan-outs, so stamping the same
 * scope count on three cards that dispatched a third each would read as three
 * failures.
 *
 * Counted on DISTINCT URLs. The dispatch unit is a test_id (url × viewport
 * cell), so items routinely outnumber scope URLs several times over — that
 * multiplicity is shown as "cells", never as a discrepancy.
 */

function Link({ label, value, sub, tone = "neutral" }) {
  const tones = {
    neutral: "text-gray-900",
    warn: "text-amber-700 bg-amber-50 px-1.5 rounded",
    bad: "text-red-700 bg-red-50 px-1.5 rounded",
  };
  return (
    <div className='flex items-baseline gap-1.5'>
      <span className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
        {label}
      </span>
      <span className={`text-sm font-medium tabular-nums ${tones[tone]}`}>
        {value}
      </span>
      {sub && <span className='text-xs text-gray-400'>{sub}</span>}
    </div>
  );
}

function Arrow() {
  return <span className='text-gray-300 select-none'>→</span>;
}

export default function RunReconciliation({ scopeRequested, fanOuts }) {
  const list = fanOuts || [];
  const dispatched = list.reduce(
    (acc, fo) => acc + (Number(fo.total_workers) || 0),
    0
  );
  const returned = list.reduce(
    (acc, fo) => acc + ((fo.workers || []).length || 0),
    0
  );
  const scopeUrls = scopeRequested?.url_count ?? null;

  // Only the links we can actually evidence. A full-tree run has no launch
  // scope file at all, so the chain legitimately starts at dispatch.
  const missingReturns = dispatched > 0 && returned < dispatched;

  return (
    <div className='bg-white border border-gray-200 rounded-xl px-5 py-3 mb-4'>
      <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
        {scopeUrls !== null && (
          <>
            <Link
              label='scope'
              value={`${scopeUrls} url${scopeUrls === 1 ? "" : "s"}`}
              sub='requested'
            />
            <Arrow />
          </>
        )}
        <Link
          label='dispatch'
          value={dispatched}
          sub={
            list.length > 1
              ? `across ${list.length} fan-outs`
              : scopeUrls && dispatched > scopeUrls
              ? `cells · ${scopeUrls} urls`
              : null
          }
        />
        <Arrow />
        <Link
          label='returned'
          value={returned}
          tone={missingReturns ? "warn" : "neutral"}
          sub={missingReturns ? `${dispatched - returned} missing` : null}
        />

        {scopeUrls === null && (
          <span
            title='No launch scope for this run (scopes/launch-<run_id>.json absent) — normal for a full-tree run, where the orchestrator picks the pages itself.'
            className='text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500'>
            no launch scope
          </span>
        )}
        {missingReturns && (
          <span className='text-xs text-amber-700'>
            fan-out still in flight, or workers lost — open artifacts to tell
            which
          </span>
        )}
      </div>
    </div>
  );
}
